import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Linkedin, Github, Mail } from "lucide-react";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Projects", href: "#projects" },
    { label: "Experience", href: "#experience" },
    { label: "Contact", href: "#contact" },
    { label: "About", href: "#about" },
  ];

  const scrollToSection = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href === "#") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleLogoClick = () => {
    setIsLogoAnimating(true);
    scrollToSection("#");
    setTimeout(() => setIsLogoAnimating(false), 600);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/80 backdrop-blur-xl shadow-lg border-b border-border/30"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleLogoClick}
              className="group transition-all duration-300 hover:scale-105 active:scale-95 flex-shrink-0"
            >
              <img
                src="/logo.png"
                alt="Spilios Spiliopoulos - AI/ML Systems & Architecture"
                className={`h-32 w-auto max-w-[600px] object-contain transition-all duration-300 group-hover:rotate-3 group-hover:drop-shadow-[0_0_12px_hsl(var(--primary)/0.5)] group-active:rotate-0 ${
                  isScrolled ? "opacity-100" : "opacity-90"
                } ${isLogoAnimating ? "animate-logo-click" : ""}`}
              />
            </button>

            {/* Desktop Navigation - positioned at far right */}
            <div className="hidden md:flex items-center gap-8 fixed right-6 lg:right-12 top-16">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`font-medium transition-all duration-300 hover:text-primary relative group ${
                    isScrolled ? "text-foreground" : "text-white dark:text-white"
                  }`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-accent transition-all duration-300 group-hover:w-full rounded-full" />
                </button>
              ))}

              {/* Social Media Icons */}
              <div className="flex items-center gap-1">
                <a
                  href="https://www.linkedin.com/in/spilios-spiliopoulos-ai-ml/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isScrolled
                      ? "text-foreground hover:text-primary hover:bg-primary/10"
                      : "text-white hover:text-white hover:bg-white/10"
                  }`}
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://github.com/spiliossp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isScrolled
                      ? "text-foreground hover:text-primary hover:bg-primary/10"
                      : "text-white hover:text-white hover:bg-white/10"
                  }`}
                  aria-label="GitHub"
                >
                  <Github className="w-5 h-5" />
                </a>
                <a
                  href="mailto:sphliossp@gmail.com"
                  className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                    isScrolled
                      ? "text-foreground hover:text-primary hover:bg-primary/10"
                      : "text-white hover:text-white hover:bg-white/10"
                  }`}
                  aria-label="Email"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="rounded-full"
              >
                {isMobileMenuOpen ? (
                  <X className={isScrolled ? "text-foreground" : "text-white"} />
                ) : (
                  <Menu className={isScrolled ? "text-foreground" : "text-white"} />
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-background/98 backdrop-blur-xl md:hidden transition-all duration-500 ${
          isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />

        <div className="flex flex-col items-center justify-center h-full space-y-8 relative z-10">
          {navLinks.map((link, index) => (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className={`text-3xl font-display font-bold text-foreground hover:text-primary transition-all duration-300 transform ${
                isMobileMenuOpen
                  ? "translate-y-0 opacity-100"
                  : "translate-y-4 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {link.label}
            </button>
          ))}
          {/* Mobile Social Media Icons */}
          <div
            className={`flex space-x-6 pt-8 transition-all duration-500 ${
              isMobileMenuOpen
                ? "translate-y-0 opacity-100"
                : "translate-y-4 opacity-0"
            }`}
            style={{ transitionDelay: "400ms" }}
          >
            <a
              href="https://www.linkedin.com/in/spilios-spiliopoulos-ai-ml/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-6 h-6" />
            </a>
            <a
              href="https://github.com/spiliossp"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110"
              aria-label="GitHub"
            >
              <Github className="w-6 h-6" />
            </a>
            <a
              href="mailto:sphliossp@gmail.com"
              className="p-3 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110"
              aria-label="Email"
            >
              <Mail className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
